/**
 * <product-variant-switcher>
 * PDP variant & option selector (migrated from the Vue `product` component).
 *
 * Wraps the option UI rendered by snippets/product-variant-switcher-generic.liquid
 * (swatches / numeric sizes / style list / dropdown / hidden system options) and owns:
 *   - resolving the picked option values to a variant id,
 *   - the "possible" (combination exists) vs "available" (exists + in stock) sets that
 *     drive each option's visibility / enabled state,
 *   - self-healing to an existing variant when a picked combination doesn't exist,
 *   - fanning the selected variant out to the other PDP components.
 *
 * Data source: window._erj_.getProductInfo(section) -> product-info-json variants[].
 * Variant-id lookup: the hidden <select id="id"> options carry data-option-1/-2/-3.
 *
 * Outbound contract:
 *   - window CustomEvent "enroute:variant-changed" { detail: { variantId, variant } }
 *     is the single fan-out mechanism. Every PDP component (product-price/gallery/
 *     description/jewelry-care/frequently-together/express-checkout, letter-customizer,
 *     custom-option-groups, wishlist-button, add-to-cart, …) subscribes in its own
 *     connectedCallback and updates itself; the switcher does not reference them.
 *   - section.product.wp.new-look dataset.selectedVariantId
 */

class ProductVariantSwitcher extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.container = this.closest("section.product.wp.new-look");
    this.section = this.container || document;

    // Currently picked value per option position (null = not chosen / option absent).
    this.option1 = null;
    this.option2 = null;
    this.option3 = null;
    this.variantId = null;

    // Mirror of the selection used by the possible/available computation.
    this.selectedOption1 = null;
    this.selectedOption2 = null;
    this.selectedOption3 = null;
    this.selectedVariantId = null;

    this.variants = null;
    this.possibleValues1 = [];
    this.possibleValues2 = [];
    this.possibleValues3 = [];
    this.availableValues1 = [];
    this.availableValues2 = [];
    this.availableValues3 = [];

    this.bindClicks();
    this.init();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  /* -------------------------------------------------------------------- *
   * Initialisation (ported from the Vue `mounted` hook)
   * -------------------------------------------------------------------- */
  init() {
    const info = window._erj_ && window._erj_.getProductInfo ? window._erj_.getProductInfo(this.section) : null;
    if (!info || !info.variants) return;
    this.variants = info.variants;

    // Seed the selection from the default variant (selected_or_first_available_variant,
    // exposed as the global `firstVariant` by product-details.liquid). Single-value
    // options render no selector, so their value only ever comes from here.
    if (typeof firstVariant !== "undefined" && firstVariant) {
      if (firstVariant.option1 + "" !== "null") this.option1 = firstVariant.option1;
      if (firstVariant.option2 + "" !== "null") this.option2 = firstVariant.option2;
      if (firstVariant.option3 + "" !== "null") this.option3 = firstVariant.option3;
    }
    this.option1 = this.option1 || null;
    this.option2 = this.option2 || null;
    this.option3 = this.option3 || null;

    this.variantId = this.findVariantIdByOptions();
    const variant = this.variantId != null ? this.variants.find((v) => v.id == this.variantId) : null;

    if (variant) {
      this.selectedOption1 = variant.option1;
      this.selectedOption2 = variant.option2;
      this.selectedOption3 = variant.option3;
      this.selectedVariantId = this.variantId;
    } else {
      this.selectedOption1 = this.option1;
      this.selectedOption2 = this.option2;
      this.selectedOption3 = this.option3;
    }

    this.computePossibleAvailable();
    this.applyOptionStates();

    if (variant) {
      // Defer the initial broadcast one frame so sibling PDP components (which register
      // their enroute:variant-changed listeners in their own connectedCallback) are ready.
      // Price/gallery/etc. already render the correct initial state server-side; this
      // broadcast is idempotent (each listener re-applies the same state) and mainly
      // refreshes price + primes listeners that hydrate from the event.
      requestAnimationFrame(() =>
        window.dispatchEvent(new CustomEvent("enroute:variant-changed", { detail: { variantId: variant.id, variant } }))
      );
    }
  }

  /* -------------------------------------------------------------------- *
   * Selection
   * -------------------------------------------------------------------- */
  bindClicks() {
    this.addEventListener(
      "click",
      (e) => {
        // Every interactive option (swatch / size / style / dropdown link / hidden
        // system <a>) carries both data-option-index and data-value; the size-grid
        // wrapper carries only data-option-index, so requiring both skips it.
        const btn = e.target.closest("[data-option-index][data-value]");
        if (!btn || !this.contains(btn) || btn.disabled) return;
        const optionIndex = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.value;
        if (!optionIndex || value == null) return;
        e.preventDefault();
        this.select(optionIndex, value, btn);
      },
      { signal: this.controller.signal }
    );

    if (!this.querySelector('[data-vs-size-dropdown]')) return;
    const signal = this.controller.signal;
    this.addEventListener('toggle', (event) => {
      const dropdown = event.target;
      if (!dropdown.matches('[data-vs-size-dropdown]')) return;
      if (dropdown.open) {
        this.querySelectorAll('[data-vs-size-dropdown]').forEach((other) => {
          if (other !== dropdown) other.open = false;
        });
      }
      this.updateSizeDropdownLabels();
    }, { capture: true, signal });
    this.addEventListener('keydown', (event) => this.onSizeDropdownKeydown(event), { signal });
    this.addEventListener('focusout', (event) => {
      const dropdown = event.target.closest('[data-vs-size-dropdown]');
      if (dropdown && !dropdown.contains(event.relatedTarget)) dropdown.open = false;
    }, { signal });
    document.addEventListener('click', (event) => {
      this.querySelectorAll('[data-vs-size-dropdown][open]').forEach((dropdown) => {
        if (!dropdown.contains(event.target)) dropdown.open = false;
      });
    }, { signal });
  }

  onSizeDropdownKeydown(event) {
    const dropdown = event.target.closest('[data-vs-size-dropdown]');
    if (!dropdown) return;
    const trigger = dropdown.querySelector('summary');
    if (event.key === 'Escape') {
      event.preventDefault();
      dropdown.open = false;
      trigger.focus({ preventScroll: true });
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const options = Array.from(dropdown.querySelectorAll('[data-vs-size-option]')).filter((option) => !option.disabled);
    if (!options.length) return;
    const wasOpen = dropdown.open;
    dropdown.open = true;
    let index = options.indexOf(document.activeElement);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = options.length - 1;
    else if (!wasOpen || index < 0) {
      index = options.findIndex((option) => option.classList.contains('active'));
      if (index < 0) index = event.key === 'ArrowUp' ? options.length - 1 : 0;
    } else {
      index = (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    }
    options[index].focus({ preventScroll: true });
    options[index].scrollIntoView({ block: 'nearest' });
  }

  updateSizeDropdownLabels() {
    this.querySelectorAll('[data-vs-size-label]').forEach((label) => {
      const value = this['option' + label.dataset.vsSizeLabel];
      const dropdown = label.closest('[data-vs-size-dropdown]');
      label.hidden = !value || dropdown.open;
      dropdown.querySelector('[data-vs-size-prompt]').hidden = Boolean(value) && !dropdown.open;
    });
  }

  select(optionIndex, value, btn) {
    // If the tentative combination has no variant, self-heal: adopt a real variant
    // that carries this value (prefer an available one), rewriting all three options.
    if (!this.variantWithOptionExists(optionIndex, value)) {
      let other = this["findAvailableVariantByOption" + optionIndex](value);
      if (!other) other = this["findPossibleVariantByOption" + optionIndex](value);
      if (other) {
        value = other["option" + optionIndex];
        this.option1 = other.option1;
        this.option2 = other.option2;
        this.option3 = other.option3;
        this.selectedOption1 = other.option1;
        this.selectedOption2 = other.option2;
        this.selectedOption3 = other.option3;
      }
    }

    // Close the legacy selector or native size dropdown after a pick.
    const selector = btn.closest(".selector, .pd2610-selector");
    if (selector) selector.classList.remove("open");
    const sizeDropdown = btn.closest('[data-vs-size-dropdown]');
    if (sizeDropdown) {
      sizeDropdown.open = false;
      sizeDropdown.querySelector('summary').focus({ preventScroll: true });
    }

    this["option" + optionIndex] = value;

    // Only adopt a new id when the picked combination actually maps to a variant
    // (mirrors the original Vue behaviour of updating the id only when a match is found).
    const found = this.findVariantIdByOptions();
    if (found != null) this.variantId = found;
    if (this.container) this.container.dataset.selectedVariantId = this.variantId;

    this.setPossibleAndAvailableOptions(optionIndex, value);
    this.applyOptionStates();

    const variant = this.variantId != null ? (this.variants || []).find((v) => v.id == this.variantId) : null;
    // Single mechanism: broadcast the selected variant and let each PDP component
    // update itself from its own enroute:variant-changed listener. The switcher no
    // longer knows which components exist — adding one is a change to that component
    // alone. detail carries both the id (express-checkout wants just the id) and the
    // full variant object (everyone else).
    if (variant) {
      window.dispatchEvent(new CustomEvent("enroute:variant-changed", { detail: { variantId: variant.id, variant } }));
    }
  }

  /* -------------------------------------------------------------------- *
   * Variant-id lookup via the hidden <select id="id"> options
   * -------------------------------------------------------------------- */
  findVariantIdByOptions() {
    const select = (this.container || document).querySelector("#id") || document.getElementById("id");
    if (!select) return null;
    const o1 = this.option1 || "";
    const o2 = this.option2 || "";
    const o3 = this.option3 || "";
    for (const opt of select.querySelectorAll("option")) {
      if (
        (opt.getAttribute("data-option-1") || "") === o1 &&
        (opt.getAttribute("data-option-2") || "") === o2 &&
        (opt.getAttribute("data-option-3") || "") === o3
      ) {
        return opt.value;
      }
    }
    return null;
  }

  /* -------------------------------------------------------------------- *
   * Possible / available computation
   * -------------------------------------------------------------------- */
  setPossibleAndAvailableOptions(optionIndex, value) {
    this["selectedOption" + optionIndex] = value;
    this.computePossibleAvailable();
    const match = (this.variants || []).find(
      (item) =>
        item.option1 === this.selectedOption1 && item.option2 === this.selectedOption2 && item.option3 === this.selectedOption3
    );
    this.selectedVariantId = match ? match.id : null;
  }

  computePossibleAvailable() {
    const variants = this.variants || [];
    const p1 = new Set();
    const p2 = new Set();
    const p3 = new Set();
    const a1 = new Set();
    const a2 = new Set();
    const a3 = new Set();

    for (const item of variants) {
      if (item.option1 != null) p1.add(item.option1);
      if (item.option1 === this.selectedOption1 && item.option2 != null) p2.add(item.option2);
      if (item.option1 === this.selectedOption1 && item.option2 === this.selectedOption2 && item.option3 != null) p3.add(item.option3);

      if (item.option1 != null && item.available) a1.add(item.option1);
      if (item.option1 === this.selectedOption1 && item.option2 != null && item.available) a2.add(item.option2);
      if (
        item.option1 === this.selectedOption1 &&
        item.option2 === this.selectedOption2 &&
        item.option3 != null &&
        item.available
      ) {
        a3.add(item.option3);
      }
    }

    this.possibleValues1 = Array.from(p1);
    this.possibleValues2 = Array.from(p2);
    this.possibleValues3 = Array.from(p3);
    this.availableValues1 = Array.from(a1);
    this.availableValues2 = Array.from(a2);
    this.availableValues3 = Array.from(a3);
  }

  isOptionPossible(option, value) {
    const arr = this["possibleValues" + option];
    return Array.isArray(arr) && arr.includes(value);
  }

  isOptionAvailable(option, value) {
    const arr = this["availableValues" + option];
    return Array.isArray(arr) && arr.includes(value);
  }

  variantWithOptionExists(index, value) {
    let o1 = this.option1;
    let o2 = this.option2;
    let o3 = this.option3;
    if (index === 1) o1 = value;
    else if (index === 2) o2 = value;
    else if (index === 3) o3 = value;
    return (this.variants || []).some((item) => item.option1 === o1 && item.option2 === o2 && item.option3 === o3);
  }

  findPossibleVariantByOption1(v) {
    return (this.variants || []).find((i) => i.option1 === v) || null;
  }
  findPossibleVariantByOption2(v) {
    return (this.variants || []).find((i) => i.option2 === v) || null;
  }
  findPossibleVariantByOption3(v) {
    return (this.variants || []).find((i) => i.option3 === v) || null;
  }
  findAvailableVariantByOption1(v) {
    return (this.variants || []).find((i) => i.option1 === v && i.available) || null;
  }
  findAvailableVariantByOption2(v) {
    return (this.variants || []).find((i) => i.option2 === v && i.available) || null;
  }
  findAvailableVariantByOption3(v) {
    return (this.variants || []).find((i) => i.option3 === v && i.available) || null;
  }

  /* -------------------------------------------------------------------- *
   * View state (replaces Vue :class / v-show / :style / [[ optionN ]])
   * -------------------------------------------------------------------- */
  applyOptionStates() {
    const variant = (this.variants || []).find((item) => item.id == this.variantId);
    const description = (variant?.variant_desc || "").trim();
    this.querySelectorAll("[data-vs-variant-description]").forEach((label) => {
      label.textContent = description;
      label.hidden = !description;
    });

    this.querySelectorAll("[data-option-index][data-value]").forEach((btn) => {
      const idx = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.value;
      const possible = this.isOptionPossible(idx, value);
      const available = this.isOptionAvailable(idx, value);
      const active = this["option" + idx] === value;

      btn.classList.toggle("active", active);
      btn.classList.toggle("is-possible", possible);
      btn.classList.toggle("is-available", available);
      btn.classList.toggle("swatch-unavailable", !possible || !available);
      btn.setAttribute("data-available", available ? "true" : "false");

      if (btn.hasAttribute('data-vs-size-option')) {
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.disabled = !possible;
        btn.tabIndex = active ? 0 : -1;
      }

      // Options that had v-show="isOptionPossible(...)" (style / swatch / size) hide
      // when the value can no longer combine with the current selection.
      if (btn.hasAttribute("data-vs-show-if-possible")) {
        btn.style.display = possible ? "" : "none";
      }

      // Dropdown links reproduce the original inline :style (grey when unavailable).
      if (btn.hasAttribute("data-vs-dropdown-link")) {
        btn.style.color = available ? "black" : "grey";
        btn.style.fontWeight = available ? "600" : "500";
      }

      // Legacy grid-selection class kept in sync with the active value.
      const wrapper = btn.closest(".hvg-option-grid-item-wrapper");
      if (wrapper) wrapper.classList.toggle("hvg-selected", active);
    });

    // Selected-value labels (were [[ optionN ]] interpolations).
    this.querySelectorAll("[data-vs-value-display]").forEach((span) => {
      const idx = parseInt(span.dataset.vsValueDisplay, 10);
      const val = this["option" + idx];
      span.textContent = val != null ? val : "";
      if (span.hasAttribute("data-vs-show-if-selected")) {
        span.style.display = val ? "" : "none";
      }
    });
    this.updateSizeDropdownLabels();
  }
}

if (!window.customElements.get("product-variant-switcher")) {
  window.customElements.define("product-variant-switcher", ProductVariantSwitcher);
}
